import React, { useState, useCallback } from 'react';
import DashboardTabBar from './components/DashboardTabBar';
import DashboardTabManager from './components/DashboardTabManager';
import { TabManagerState, CreateTabOptions, TabState } from './types/tabs.types';
import {
  loadTabManagerState,
  createNewTab,
  switchToTab,
  updateTabState,
  deleteTab,
  renameTab,
  saveTabManagerState,
} from './utils/storageUtils';
import 'react-grid-layout/css/styles.css';

const MarketAggregatesNewContent: React.FC = () => {
  // Tab manager state
  const [tabManagerState, setTabManagerState] = useState<TabManagerState>(() => {
    return loadTabManagerState();
  });

  const handleTabSwitch = useCallback((tabId: string) => {
    setTabManagerState((prev) => {
      if (tabId === prev.activeTabId) return prev;
      return switchToTab(prev, tabId);
    });
  }, []);

  const handleTabCreate = useCallback((options: CreateTabOptions) => {
    setTabManagerState((prev) => createNewTab(prev, options));
  }, []);

  const handleTabDelete = useCallback((tabId: string) => {
    setTabManagerState((prev) => deleteTab(prev, tabId) ?? prev);
  }, []);

  const handleTabRename = useCallback((tabId: string, newName: string) => {
    setTabManagerState((prev) => renameTab(prev, tabId, newName));
  }, []);

  const handleTabReorder = useCallback((fromIndex: number, toIndex: number) => {
    setTabManagerState((prev) => {
      const newTabs = [...prev.tabs];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);
      const next = { ...prev, tabs: newTabs };
      saveTabManagerState(next);
      return next;
    });
  }, []);

  const handleTabStateChange = useCallback((tabId: string, updates: Partial<TabState>) => {
    setTabManagerState((prev) => updateTabState(prev, tabId, updates));
  }, []);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Tab Bar */}
      <DashboardTabBar
        tabs={tabManagerState.tabs}
        activeTabId={tabManagerState.activeTabId}
        onTabSwitch={handleTabSwitch}
        onTabCreate={handleTabCreate}
        onTabDelete={handleTabDelete}
        onTabRename={handleTabRename}
        onTabReorder={handleTabReorder}
      />

      {/* Tab Content */}
      <div className="flex-1 min-h-0 relative">
        <DashboardTabManager
          tabs={tabManagerState.tabs}
          activeTabId={tabManagerState.activeTabId}
          tabStates={tabManagerState.tabStates}
          onStateChange={handleTabStateChange}
        />
      </div>
    </div>
  );
};

const MarketAggregatesNew: React.FC = () => {
  return <MarketAggregatesNewContent />;
};

export default MarketAggregatesNew;
